import { getComposeStore } from 'owa-calendar-compose-store-manager';
import cloneEvent from '../utils/cloneEvent';
import preserveAttendeeData from '../utils/preserveAttendeeData';
import getQuickComposeViewState from '../selectors/getQuickComposeViewState';
import { updateStartEndWithTimeZone } from 'owa-calendar-types';
import type { ClientAttachmentId } from 'owa-client-ids';
import { isBefore, isEqual } from 'owa-datetime';
import { isOnGroupCalendar } from 'owa-calendar-event-capabilities';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import { AttachmentStatusEnum } from 'owa-addins-core';
import triggerAttachmentsChanged from '../utils/triggerAttachmentsChanged';
import * as trace from 'owa-trace';
import { mutator } from 'satcheljs';
import {
    initializeCalendarEvents,
    updateCalendarEvents,
    onAllowForwardingChanged,
    onIsDraftChanged,
    onAttachmentCreated,
    onAttachmentDeleted,
    onAttachmentsCleared,
    onReminderChanged,
    onRequestResponseChanged,
    onSensitivityToggled,
    setEventBodyFromViewState,
    updateCharm,
    updateFreeBusy,
    updateSubject,
    onIsOnlineMeetingChanged,
    onOptionalAttendeesChanged,
    onRequiredAttendeesChanged,
    onResourcesChanged,
    onCategoriesChanged,
    updateCharmAndSelectionType,
    onLocationsChanged,
    updateCharmSelectionType,
    updateOpenedForm,
    onDateTimesChanged,
    onAllDayChanged,
    onParentFolderIdChanged,
    onHideAttendeeListChanged,
    onSkypeTeamsPropertiesChanged,
    onRoomRequestedChanged,
    onUpdateBodyValueOfEvent,
    onCollabSpaceUpdated,
    setRecurrence,
} from 'owa-calendar-compose-form-actions';
import getFullComposeViewState from '../selectors/getFullComposeViewState';

let assign = require('object-assign');

/**
 * Provides a starting ground for edit scenarios.
 */
export const initializeCalendarEvent = mutator(
    initializeCalendarEvents,
    ({ originalEvent, eventUpdates }) => {
        const store = getComposeStore();

        // The calendar event needs to be cloned to break away from the notification chain. Updates
        // to the event should not overwrite what the user is modifying.
        store.event = cloneEvent(originalEvent);
        if (eventUpdates) {
            assign(store.event, eventUpdates);
        }

        // Likewise, cloning the original event provides a snapshot at edit beginning. This is used
        // before saving to tell what the user's changes were.
        store.originalEvent = cloneEvent(originalEvent);

        updateStartEndWithTimeZone(store.event);
        updateStartEndWithTimeZone(store.originalEvent);
    }
);

/**
 * Updates the events.
 */
export const updateCalendarEvent = mutator(updateCalendarEvents, ({ event }) => {
    const store = getComposeStore();

    // update events if the form is still open
    if (store.openedForm) {
        store.event = cloneEvent(event);
        store.originalEvent = cloneEvent(event);
        updateStartEndWithTimeZone(store.event);
        updateStartEndWithTimeZone(store.originalEvent);
    }
});

/**
 * Updates the opened form in the calendar event
 */
export let updateOpenedFormMutators = mutator(updateOpenedForm, actionMessage => {
    let store = getComposeStore();
    if (store) {
        store.openedForm = actionMessage.openedForm;
    }
});

/**
 * Updates the charm in the calendar event
 */
export let charmMutator = mutator(updateCharm, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.CharmId = actionMessage.iconId;
    }
});

/**
 * Updates the locations on the calendar event
 */
export let locationsMutator = mutator(onLocationsChanged, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.Locations = actionMessage.locations;
    }
});

/**
 * Updates the charm selection type for the charm
 */
export let charmSelectionTypeMutator = mutator(updateCharmSelectionType, actionMessage => {
    let uiState = getQuickComposeViewState();
    if (uiState) {
        uiState.charmSelection = actionMessage.charmSelectionType;
    }
});

/**
 * Updates the charm on the event and the charm selection type on the quick compose view state
 */
export let charmAndSelectionTypeMutator = mutator(updateCharmAndSelectionType, actionMessage => {
    let event = getComposeStore().event;
    let uiState = getQuickComposeViewState();

    if (event) {
        event.CharmId = actionMessage.iconId;
        uiState.charmSelection = actionMessage.charmSelection;
    }
});

/**
 * Updates the free busy (show as) in the calendar event
 */
export let freeBusyMutator = mutator(updateFreeBusy, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.FreeBusyType = actionMessage.busyType;
    }
});

/**
 * Updates IsDraft flag in the calendar event
 */
export let onIsDraftChangedMutator = mutator(onIsDraftChanged, actionMessage => {
    let item = getComposeStore().event;
    if (item) {
        item.IsDraft = actionMessage.isDraftEvent;
    }
});

/**
 * Updates the reminder in the calendar event and sets ReminderIsSet to match
 */
export let reminderMinutesMutator = mutator(onReminderChanged, actionMessage => {
    let event = getComposeStore().event;
    if (!event) {
        return;
    }

    // Cannot set -1 into ReminderMinutesBeforeStart or Desktop will break
    if (actionMessage.reminderMinutesBeforeStart != -1) {
        event.ReminderMinutesBeforeStart = actionMessage.reminderMinutesBeforeStart;
    }

    event.ReminderIsSet =
        event.ReminderMinutesBeforeStart != undefined &&
        event.ReminderMinutesBeforeStart != null &&
        actionMessage.reminderMinutesBeforeStart != -1;
});

/**
 * Updates the sensitivity in the calendar event
 */
export let sensitivityMutator = mutator(onSensitivityToggled, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.Sensitivity = event.Sensitivity === 'Normal' ? 'Private' : 'Normal';
    }
});

/**
 * Updates the subject in the calendar event
 */
export let subjectMutator = mutator(updateSubject, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.Subject = actionMessage.subject;
    }
});

/**
 * Updates the Do Not Forward flag in the calendar event
 */
export let doNotForwardMeetingMutator = mutator(onAllowForwardingChanged, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.DoNotForwardMeeting = actionMessage.DoNotForwardMeeting;
    }
});

/**
 * Updates the Response Requested flag in the calendar event
 */
export let isResponseRequestedMutator = mutator(onRequestResponseChanged, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.IsResponseRequested = actionMessage.IsResponseRequested;
    }
});

/**
 * Updates the HideAttendees flag in the calendar event
 */
export let hideAttendeesMutator = mutator(onHideAttendeeListChanged, actionMessage => {
    let event = getComposeStore().event;
    event.HideAttendees = actionMessage.HideAttendees;
});

/**
 * Sets the ParentFolderId property.
 */
export const parentFolderIdMutator = mutator(onParentFolderIdChanged, actionMessage => {
    const { event } = getComposeStore();
    if (actionMessage.ParentFolderId) {
        event.ParentFolderId = actionMessage.ParentFolderId;
        if (isOnGroupCalendar(event)) {
            event.Sensitivity = 'Normal';
        }
    }
});

/**
 * Sets the body of the event from the view state used by React Editor
 */
export const setEventBodyFromViewStateMutator = mutator(setEventBodyFromViewState, () => {
    const { event, uiState } = getComposeStore();
    if (uiState) {
        const { content, isDirty } = uiState.bodyViewState;

        // Set body type to be HTML even when bodyViewState is not dirty since attachments fail if BodyType is Text
        // Note: Body type also needs to be HTML if we want online meeting blob to html formatted
        event.Body = {
            Value: isDirty ? content : event.Body ? event.Body.Value : '',
            BodyType: 'HTML',
        };
    }
});

/**
 * Sets the IsOnlineMeeting property based on the Add "Skype/teams" button being clicked.
 */
export const isOnlineMeetingMutator = mutator(onIsOnlineMeetingChanged, actionMessage => {
    const { event } = getComposeStore();
    if (event) {
        event.IsOnlineMeeting = actionMessage.IsOnlineMeeting;
        event.OnlineMeetingProvider = actionMessage.onlineMeetingProvider;
    }
});

/**
 * Sets the RequiredAttendees on Add/Remove action from Full compose form
 */
export const onRequiredAttendeesChangedMutator = mutator(
    onRequiredAttendeesChanged,
    actionMessage => {
        let event = getComposeStore().event;
        if (event) {
            // Attendees in the incoming set may only have Mailbox info.
            // Instead of overwriting the existing set, we should preserve data on attendees that exist in both sets.
            event.RequiredAttendees = preserveAttendeeData(
                event.RequiredAttendees,
                actionMessage.requiredAttendees as AttendeeType[]
            );
        }
    }
);

/**
 * Sets the room booking assistant on Add/Remove action from full compose form
 */
mutator(onRoomRequestedChanged, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.IsRoomRequested = actionMessage.isRoomRequested;
    }
});

/**
 * Sets the OptionalAttendees on Add/Remove action from Full compose form
 */
export const onOptionalAttendeesChangedMutator = mutator(
    onOptionalAttendeesChanged,
    actionMessage => {
        let event = getComposeStore().event;
        if (event) {
            // Attendees in the incoming set may only have Mailbox info.
            // Instead of overwriting the existing set, we should preserve data on attendees that exist in both sets.
            event.OptionalAttendees = preserveAttendeeData(
                event.OptionalAttendees,
                actionMessage.optionalAttendees as AttendeeType[]
            );
        }
    }
);

/**
 * Sets the OptionalAttendees on Add/Remove action from Full compose form
 */
export const onResourcesChangedMutator = mutator(onResourcesChanged, actionMessage => {
    let event = getComposeStore().event;
    if (event) {
        event.Resources = actionMessage.resources as AttendeeType[];
    }
});

/**
 * Updates the attachment list to add the newly created attachment
 */
export const attachmentAddedMutator = mutator(
    onAttachmentCreated,
    (actionMessage: { attachmentId: ClientAttachmentId; attachment: AttachmentType }) => {
        const event = getComposeStore().event;
        if (event) {
            event.Attachments.push(actionMessage.attachment);
        }
        triggerAttachmentsChanged(AttachmentStatusEnum.Added, actionMessage.attachment);
    }
);

/**
 * Updates the attachment list to remove the deleted attachment
 */
export const attachmentDeletedMutator = mutator(
    onAttachmentDeleted,
    (actionMessage: { attachmentId: ClientAttachmentId; attachment: AttachmentType }) => {
        const event = getComposeStore().event;
        if (event) {
            event.Attachments = event.Attachments.filter(
                att => att.AttachmentId.Id !== actionMessage.attachmentId.Id
            );
        }
        triggerAttachmentsChanged(AttachmentStatusEnum.Deleted, actionMessage.attachment);
    }
);

/**
 * Updates the attachment list to remove the deleted attachment
 */
export const attachmentsClearedMutator = mutator(onAttachmentsCleared, () => {
    const event = getComposeStore().event;
    if (event) {
        const existingAttachments = event.Attachments;
        event.Attachments = [];
        existingAttachments.forEach(a =>
            triggerAttachmentsChanged(AttachmentStatusEnum.Deleted, a)
        );
    }
});

/**
 * Updates the categories array to the user selection
 */
export const categoriesMutator = mutator(
    onCategoriesChanged,
    (actionMessage: { Categories: string[] }) => {
        const event = getComposeStore().event;
        if (event) {
            event.Categories = actionMessage.Categories;
        }
    }
);

/**
 * Validates that start and end times are valid and replaces them in the Calendar Event
 */

export const dateTimesMutator = mutator(onDateTimesChanged, ({ datesSelected: { start, end } }) => {
    const event = getComposeStore().event;
    const fullComposeViewState = getFullComposeViewState();

    /*
        Do not update time in case uiEnabled is set false for FullComposeView state
    */
    if (event && fullComposeViewState ? fullComposeViewState.uiEnabled : true) {
        if (isBefore(end, start)) {
            trace.errorThatWillCauseAlert('Cannot set the end time to be before the start time');
        } else {
            if (!isEqual(event.Start, start) || event.Start.tz != start.tz) {
                event.Start = start;
            }
            if (!isEqual(event.End, end) || event.End.tz != end.tz) {
                event.End = end;
            }
            event.StartTimeZoneId = start.tz;
            event.EndTimeZoneId = end.tz;
        }
    }
});

/**
 * Updates the all day value in the calendar event
 */
export const isAllDayMutator = mutator(
    onAllDayChanged,
    (actionMessage: { isAllDayEvent: boolean }) => {
        const event = getComposeStore().event;
        if (event) {
            event.IsAllDayEvent = actionMessage.isAllDayEvent;
        }
    }
);

/**
 * Sets the skype/teams properties
 */
export const onSkypeTeamsPropertiesChangedMutator = mutator(
    onSkypeTeamsPropertiesChanged,
    actionMessage => {
        let event = getComposeStore().event;
        if (event) {
            event.SkypeTeamsProperties = actionMessage.skypeTeamsProperties;
        }
    }
);

export const onUpdateBodyValueOfEventMutator = mutator(onUpdateBodyValueOfEvent, actionMessage => {
    let { event } = getComposeStore();
    if (event) {
        event.Body.Value = actionMessage.Value;
        event.HasBlockedImages = actionMessage.HasBlockedImages;
    }
});

export const onCollabSpaceUpdatedMutator = mutator(onCollabSpaceUpdated, actionMessage => {
    let { event } = getComposeStore();
    if (event) {
        event.CollabSpace = actionMessage.collabSpace;
    }
});

export const setRecurrenceMutator = mutator(setRecurrence, actionMessage => {
    let { event } = getComposeStore();
    if (event) {
        event.Recurrence = actionMessage.recurrenceType;
    }
});

export type { default as EnhancedLocation } from 'owa-service/lib/contract/EnhancedLocation';
export { CharmSelectionType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
